import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { saveTranslations, loadTranslations } from "./translations"

const DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]

const HoursEditor = () => {
  const { i18n } = useTranslation("global")
  const [hours, setHours] = useState([])
  const [isSaving, setIsSaving] = useState(false)
  const [message, setMessage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [itTranslations, setItTranslations] = useState(null)
  const [enTranslations, setEnTranslations] = useState(null)

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const [itData, enData] = await Promise.all([
          loadTranslations("it"),
          loadTranslations("en"),
        ])

        if (
          !itData || !enData ||
          Array.isArray(itData) || Array.isArray(enData) ||
          typeof itData !== "object" || typeof enData !== "object" ||
          Object.keys(itData).length === 0 || Object.keys(enData).length === 0
        ) {
          throw new Error("Failed to load translations: received empty or invalid data. Try refreshing.")
        }

        setItTranslations(itData)
        setEnTranslations(enData)

        const itHours = itData?.home?.hours || {}
        const enHours = enData?.home?.hours || {}

        const merged = DAYS.map((day) => ({
          day,
          dayNameIt: itHours[`${day}Name`] || "",
          dayNameEn: enHours[`${day}Name`] || "",
          hoursIt: itHours[day] || "",
          hoursEn: enHours[day] || "",
        }))

        setHours(merged)
      } catch (error) {
        console.error("[HoursEditor] Error loading:", error)
        setMessage({ type: "error", text: "Failed to load hours: " + error.message })
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const updateHour = (index, field, value) => {
    setHours((prev) =>
      prev.map((h, i) => (i === index ? { ...h, [field]: value } : h))
    )
  }

  const handleSave = async (e) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (!itTranslations || !enTranslations) {
      setMessage({ type: "error", text: "Translations not loaded." })
      return
    }

    setIsSaving(true)
    setMessage({ type: "loading", text: "Saving hours..." })

    try {
      const updatedIt = JSON.parse(JSON.stringify(itTranslations))
      const updatedEn = JSON.parse(JSON.stringify(enTranslations))

      if (!updatedIt.home) updatedIt.home = {}
      if (!updatedIt.home.hours) updatedIt.home.hours = {}
      if (!updatedEn.home) updatedEn.home = {}
      if (!updatedEn.home.hours) updatedEn.home.hours = {}

      hours.forEach(({ day, dayNameIt, dayNameEn, hoursIt, hoursEn }) => {
        updatedIt.home.hours[`${day}Name`] = dayNameIt
        updatedIt.home.hours[day] = hoursIt
        updatedEn.home.hours[`${day}Name`] = dayNameEn
        updatedEn.home.hours[day] = hoursEn
      })

      await saveTranslations("it", updatedIt)
      await saveTranslations("en", updatedEn)

      i18n.addResourceBundle("it", "global", updatedIt, true, true)
      i18n.addResourceBundle("en", "global", updatedEn, true, true)

      setItTranslations(updatedIt)
      setEnTranslations(updatedEn)

      setMessage({ type: "success", text: "Hours saved successfully! Reloading..." })
      setTimeout(() => window.location.reload(), 2000)
    } catch (error) {
      console.error("[HoursEditor] Save error:", error)
      setMessage({ type: "error", text: "Failed to save: " + error.message })
      setIsSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading hours...</div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto" style={{ maxHeight: "calc(100vh - 100px)" }}>
      <div className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-heading text-secondary-200">Hours Editor</h1>
            <p className="text-gray-600 text-xs sm:text-sm">
              Edit opening hours for each day. Changes update both Footer and Home page.
            </p>
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className={`w-full sm:w-auto px-6 py-2 rounded-lg font-medium transition-colors ${
              isSaving
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-secondary-200 text-white hover:bg-secondary-400"
            }`}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>

        {message && (
          <div
            className={`mb-4 p-3 rounded-lg ${
              message.type === "success"
                ? "bg-green-100 text-green-700"
                : message.type === "error"
                  ? "bg-red-100 text-red-700"
                  : "bg-blue-100 text-blue-700"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="space-y-3">
          {hours.map((h, index) => (
            <div
              key={h.day}
              className="bg-white rounded-lg border-2 border-gray-200 p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Day Names */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Day Name</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="inline-flex items-center gap-1 mb-1">
                        <span className="w-4 h-3 rounded bg-green-600 text-white text-[8px] flex items-center justify-center font-bold">
                          IT
                        </span>
                      </span>
                      <input
                        type="text"
                        value={h.dayNameIt}
                        onChange={(e) => updateHour(index, "dayNameIt", e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <span className="inline-flex items-center gap-1 mb-1">
                        <span className="w-4 h-3 rounded bg-blue-600 text-white text-[8px] flex items-center justify-center font-bold">
                          EN
                        </span>
                      </span>
                      <input
                        type="text"
                        value={h.dayNameEn}
                        onChange={(e) => updateHour(index, "dayNameEn", e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Hours */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Hours</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="inline-flex items-center gap-1 mb-1">
                        <span className="w-4 h-3 rounded bg-green-600 text-white text-[8px] flex items-center justify-center font-bold">
                          IT
                        </span>
                      </span>
                      <input
                        type="text"
                        value={h.hoursIt}
                        onChange={(e) => updateHour(index, "hoursIt", e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                        placeholder="e.g. 18:30 – 22:00"
                      />
                    </div>
                    <div>
                      <span className="inline-flex items-center gap-1 mb-1">
                        <span className="w-4 h-3 rounded bg-blue-600 text-white text-[8px] flex items-center justify-center font-bold">
                          EN
                        </span>
                      </span>
                      <input
                        type="text"
                        value={h.hoursEn}
                        onChange={(e) => updateHour(index, "hoursEn", e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-secondary-400 focus:border-transparent text-sm"
                        placeholder="e.g. 18:30 – 22:00"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="font-semibold text-amber-800 mb-2">How to use:</h3>
          <ol className="text-amber-700 text-sm space-y-1 list-decimal list-inside">
            <li>Edit day names and hours for each day of the week</li>
            <li>For closed days, enter "Chiuso" (IT) / "Closed" (EN)</li>
            <li>For days with morning + evening, use newline: "11:00 – 14:00 \n 18:30 – 22:00"</li>
            <li>Click "Save Changes" to update both languages on the server</li>
            <li>The Footer and Home page hours grid will update automatically</li>
          </ol>
        </div>
      </div>
    </div>
  )
}

export default HoursEditor
